// Frontend Sentry initialisation. Imported and called from main.tsx
// before React renders so that even very-early render errors are caught.
//
// Without VITE_SENTRY_DSN_FRONTEND configured this is a complete no-op:
// Sentry.init() is skipped and the exported ErrorBoundary falls back to
// React's normal behaviour (re-throw to the next boundary). This keeps
// dev silent and free.

import * as Sentry from "@sentry/react";

const dsn = (import.meta as { env?: Record<string, string | undefined> }).env
  ?.VITE_SENTRY_DSN_FRONTEND;

const mode = (import.meta as { env?: Record<string, string | undefined> }).env
  ?.MODE ?? "development";

export const sentryEnabled = Boolean(dsn);

export function initSentry(): void {
  if (!dsn) return;
  Sentry.init({
    dsn,
    environment: mode,
    // Browser tracing samples a fraction of pageloads / navigations so we
    // can see which routes are slow without paying for 100% volume.
    tracesSampleRate: 0.1,
    // Replay disabled by default — opt in later if needed (it ships ~50KB
    // of extra JS and records DOM mutations). Errors are captured without
    // replay just fine.
    integrations: [Sentry.browserTracingIntegration()],
    // Don't send default PII — we forward only what we explicitly set
    // via Sentry.setUser({ id: <hashed founder/banker id> }) when the
    // session resolves.
    sendDefaultPii: false,
    // Scrub anything that smells sensitive before it leaves the browser.
    beforeSend(event) {
      // `cookies` is typed as Record<string, string>; replacing each value
      // (instead of the whole object) keeps the type contract valid.
      if (event.request?.cookies) {
        for (const k of Object.keys(event.request.cookies)) {
          event.request.cookies[k] = "[redacted]";
        }
      }
      if (event.request?.headers) {
        const h = event.request.headers as Record<string, string>;
        if (h["authorization"]) h["authorization"] = "[redacted]";
        if (h["cookie"]) h["cookie"] = "[redacted]";
      }
      return event;
    },
    ignoreErrors: [
      // ResizeObserver loop limit exceeded — benign browser noise that
      // every Sentry account learns to hate within a week.
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      // Network blips on hot-reload in dev iframes.
      "Failed to fetch",
      "Load failed",
      "NetworkError when attempting to fetch resource",
    ],
  });
}

export { Sentry };
